import { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Clickable from 'components/Common/Clickable'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineArrowsSwapHorizontalIcon from 'LuxKit/Icons/line/LineArrowsSwapHorizontalIcon'
import { getCheckInCheckOutText } from 'components/Search/utils'
import useOrderFlightDetails from 'hooks/Flights/useOrderFlightDetails'
import moment from 'moment'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { deriveFlightDisplayData, formatFlightFallbackTitle, formatFlightTypeLabel } from './flightDisplayUtils'

const Container = styled(Clickable)`
  width: ${rem(240)};
  min-height: ${rem(110)};
  flex-shrink: 0;

  > * {
    height: 100%;
  }
`

interface Props {
  itemId: string
  orderId: string
  selected?: boolean
  onClick: (itemId: string) => void
}

const AirlineLogo = styled.img`
  width: ${rem(28)};
  height: ${rem(28)};
  object-fit: contain;
  flex-shrink: 0;
`

export default function ChatLandingFlightOrderTile({
  itemId,
  orderId,
  selected,
  onClick,
}: Props) {
  const intl = useIntl()
  const [{ flightDetails }] = useOrderFlightDetails(orderId, [itemId])

  const {
    departureDate,
    arrivalDate,
    departureLabel,
    arrivalLabel,
    airlineLogoUrl,
    isReturnFlight,
    isDirectFlight,
  } = deriveFlightDisplayData(flightDetails)

  const subtitleParts: Array<string> = []
  if (departureDate && arrivalDate && isReturnFlight) {
    subtitleParts.push(getCheckInCheckOutText(departureDate, arrivalDate))
  } else if (departureDate) {
    subtitleParts.push(moment(departureDate).format(DMY_CASUAL_SHORT_FORMAT))
  }
  subtitleParts.push(formatFlightTypeLabel(intl, isReturnFlight, isDirectFlight))
  const subtitle = subtitleParts.join(' · ')

  const routeIcon = isReturnFlight ? <LineArrowsSwapHorizontalIcon size="S" /> : <LineArrowRightIcon size="S" />
  const hasRoute = Boolean(departureLabel && arrivalLabel)
  const fallbackTitle = formatFlightFallbackTitle(intl)
  const yourFlightLabel = intl.formatMessage({
    id: 'abIxvLb_sFmDPtI99kll',
    defaultMessage: 'Your flight',
    description: 'Category label for flight booking tile on the chat landing page',
  })

  const handleClick = useCallback(() => {
    onClick(itemId)
  }, [onClick, itemId])

  return (
    <Container onClick={handleClick}>
      <Sheet background="neutral-eight" border={selected ? 'neutral-one' : 'neutral-five'} borderRadius="L" padding={12}>
        <Group direction="vertical" gap={12} verticalAlign="center" fullHeight>
          <CaptionTextBlock
            variant="small"
            weight="bold"
            format="uppercase"
            colour="neutral-three"
            startIcon={<LinePlaneDepartureIcon size="XS" />}
          >
            {yourFlightLabel}
          </CaptionTextBlock>
          <Group direction="horizontal" gap={8} verticalAlign="center" fullWidth>
            {airlineLogoUrl && <AirlineLogo src={airlineLogoUrl} alt="" aria-hidden="true" />}
            <Group direction="vertical" gap={2} grow>
              {hasRoute && (
                <BodyText variant="medium" weight="normal" lineClamp={2}>
                  {departureLabel} {routeIcon} {arrivalLabel}
                </BodyText>
              )}
              {!hasRoute && (
                <BodyText variant="medium" weight="normal" lineClamp={2}>
                  {fallbackTitle}
                </BodyText>
              )}
              {subtitle && (
                <Caption variant="medium" colour="neutral-three" lineClamp={1}>
                  {subtitle}
                </Caption>
              )}
            </Group>
          </Group>
        </Group>
      </Sheet>
    </Container>
  )
}
